import { effectiveDirection, effectiveLeverage, indicatorAutoLabel } from "@strat/format";
import type { BacktestResult, StrategySpec } from "@strat/format";

import { conditionText } from "../../utils/conditionText";
import {
  formatDate,
  formatDateCompact,
  formatDateTime,
  formatMoney,
  formatPrice,
  formatSignedPct,
} from "../../utils/format";
import { cell, toneOf } from "./model";
import type { ReportDoc, ReportSection, ReportStat, ReportTable } from "./model";

/**
 * The two reports: a backtest's and a live (paper) bot's. Both read the same
 * way — who and what, the headline numbers, equity and drawdown, the rules,
 * then every trade — so one can be laid next to the other.
 */

const DISCLAIMER =
  "StrategyHive · Simulated results on historical or paper data. Not investment advice; past performance does not predict future results.";

const EXIT_LABEL: Record<string, string> = {
  signal: "Signal",
  stop_loss: "Stop loss",
  take_profit: "Take profit",
  trailing_stop: "Trailing stop",
  liquidation: "Liquidated",
  end_of_data: "End of data",
  close_all: "Closed by operator",
  shutdown: "Bot shut down",
};

const MONTHS = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];

/** What a trades table needs; the engine's `Trade` and the hub's bot trades both fit. */
export interface ReportTrade {
  seq: number;
  direction: string;
  entryTime: number;
  exitTime: number;
  entryPrice: number;
  exitPrice: number;
  pnl: number;
  /** A fraction: 0.012 is +1.2%. */
  pnlPct: number;
  exitReason: string | null;
  bars?: number;
}

function ratio(value: number | null | undefined, digits = 2): string {
  return value === null || value === undefined || !Number.isFinite(value) ? "-" : value.toFixed(digits);
}

function money(value: number): string {
  return Number.isFinite(value) ? formatMoney(value) : "-";
}

function exitLabel(reason: string | null): string {
  if (!reason) return "";
  return EXIT_LABEL[reason] ?? reason.replace(/_/g, " ");
}

function tradesTable(trades: readonly ReportTrade[], title = "Trades"): ReportTable {
  const hasBars = trades.some((t) => t.bars !== undefined);
  const head = ["#", "Side", "Entry", "Exit", "Entry price", "Exit price"];
  if (hasBars) head.push("Bars");
  head.push("P&L", "P&L %", "Exit reason");
  const align: ("left" | "right")[] = ["right", "left", "left", "left", "right", "right"];
  if (hasBars) align.push("right");
  align.push("right", "right", "left");
  return {
    kind: "table",
    title: `${title} (${trades.length})`,
    head,
    align,
    fontSize: 7,
    empty: "No closed trades.",
    rows: [...trades]
      .sort((a, b) => a.seq - b.seq)
      .map((t) => {
        const row = [
          cell(String(t.seq)),
          cell(t.direction === "short" ? "Short" : "Long"),
          cell(formatDateTime(t.entryTime)),
          cell(formatDateTime(t.exitTime)),
          cell(formatPrice(t.entryPrice)),
          cell(formatPrice(t.exitPrice)),
        ];
        if (hasBars) row.push(cell(t.bars === undefined ? "" : String(t.bars)));
        row.push(
          cell(money(t.pnl), toneOf(t.pnl)),
          cell(formatSignedPct(t.pnlPct * 100), toneOf(t.pnlPct)),
          cell(exitLabel(t.exitReason)),
        );
        return row;
      }),
  };
}

function rulesSection(spec: StrategySpec): ReportSection {
  const r = spec.risk;
  const size =
    r.sizeMode === "pct_equity"
      ? `${r.sizeValue}% of equity`
      : `${r.sizeValue} ${r.sizeMode === "fixed_quote" ? "quote" : "units"}`;
  const exits = [
    r.stopLossPct ? `stop loss ${r.stopLossPct}%` : null,
    r.takeProfitPct ? `take profit ${r.takeProfitPct}%` : null,
    r.trailingStopPct ? `trailing stop ${r.trailingStopPct}%` : null,
  ].filter(Boolean);
  const costs = [
    r.feePct !== undefined ? `fee ${r.feePct}%` : null,
    r.slippagePct !== undefined ? `slippage ${r.slippagePct}%` : null,
  ].filter(Boolean);
  const indicators = spec.indicators.map((d) => d.label?.trim() || indicatorAutoLabel(d, spec.indicators));
  return {
    kind: "text",
    title: "Rules",
    lines: [
      { label: "Direction", text: effectiveDirection(spec) === "short" ? "Short" : "Long" },
      { label: "Indicators", text: indicators.join(", ") || "None (price rules only)" },
      { label: "Entry", text: conditionText(spec.entry, spec.indicators) },
      { label: "Exit", text: conditionText(spec.exit, spec.indicators) },
      {
        label: "Risk",
        text: [
          `${size} per trade`,
          effectiveLeverage(r) > 1 ? `${effectiveLeverage(r)}x leverage` : null,
          ...exits,
          ...costs,
        ]
          .filter(Boolean)
          .join(" · "),
      },
    ],
  };
}

/** Drawdown in percent (≤ 0) from an equity series, point by point. */
function drawdownSeries(points: [number, number][]): [number, number][] {
  let peak = -Infinity;
  return points.map(([t, e]) => {
    if (e > peak) peak = e;
    return [t, peak > 0 ? ((e - peak) / peak) * 100 : 0];
  });
}

function pctAxis(v: number): string {
  return `${v.toFixed(Math.abs(v) >= 10 ? 0 : 1)}%`;
}

function moneyAxis(v: number): string {
  const abs = Math.abs(v);
  if (abs >= 1e6) return `${(v / 1e6).toFixed(1)}M`;
  if (abs >= 1e4) return `${(v / 1e3).toFixed(0)}k`;
  if (abs >= 1e3) return `${(v / 1e3).toFixed(1)}k`;
  return v.toFixed(0);
}

function now(): string {
  return formatDateTime(Date.now());
}

// --- backtest -----------------------------------------------------------------------------------

export interface BacktestReportInput {
  result: BacktestResult;
  strategyName: string;
  symbol: string;
  timeframe: string;
  marketType: string;
  initialCapital?: number;
  spec?: StrategySpec;
  /** A title of its own, when the run has one (a published benchmark card). */
  title?: string | null;
}

export function backtestReport(input: BacktestReportInput): ReportDoc {
  const { result, spec } = input;
  const { stats, analytics, trades } = result;
  const first = result.equityCurve[0];
  const last = result.equityCurve[result.equityCurve.length - 1];
  const start = analytics?.startTime ?? first?.time;
  const end = analytics?.endTime ?? last?.time;
  const capital = input.initialCapital ?? first?.equity;
  const hasTrades = stats.totalTrades > 0;

  const meta: [string, string][] = [
    ["Strategy", input.strategyName],
    ["Market", `${input.symbol} ${input.timeframe} · ${input.marketType}`],
    [
      "Period",
      start !== undefined && end !== undefined
        ? `${formatDate(start)} -> ${formatDate(end)}${analytics ? ` (${analytics.days.toFixed(0)} days)` : ""}`
        : "-",
    ],
    ["Starting capital", capital !== undefined ? money(capital) : "-"],
    ["Bars tested", result.barsTested.toLocaleString("en-US")],
    ["Engine", `${result.engine.name} ${result.engine.version}`],
  ];
  if (spec && effectiveLeverage(spec.risk) > 1) meta.push(["Leverage", `${effectiveLeverage(spec.risk)}x`]);

  const returns: ReportStat[] = [
    {
      label: "Total return",
      value: formatSignedPct(stats.totalReturnPct),
      tone: toneOf(stats.totalReturnPct),
    },
    { label: "Net P&L", value: money(stats.totalPnl), tone: toneOf(stats.totalPnl) },
    { label: "Final equity", value: money(stats.finalEquity) },
    { label: "Exposure", value: `${stats.exposurePct.toFixed(1)}%` },
  ];
  if (analytics) {
    returns.splice(
      1,
      0,
      { label: "CAGR", value: formatSignedPct(analytics.cagrPct), tone: toneOf(analytics.cagrPct) },
      {
        label: analytics.benchmarkLabel ? `Hold ${analytics.benchmarkLabel}` : "Buy & hold",
        value: formatSignedPct(analytics.buyHoldReturnPct),
        tone: toneOf(analytics.buyHoldReturnPct),
      },
      {
        label: "vs buy & hold",
        value: formatSignedPct(analytics.excessReturnPct),
        tone: toneOf(analytics.excessReturnPct),
      },
      { label: "Volatility", value: `${analytics.volatilityPct.toFixed(1)}%` },
    );
  }
  if (stats.totalFunding) {
    returns.push({ label: "Funding", value: money(stats.totalFunding), tone: toneOf(stats.totalFunding) });
  }

  const risk: ReportStat[] = [
    { label: "Max drawdown", value: `-${stats.maxDrawdownPct.toFixed(2)}%`, tone: "down" },
  ];
  if (analytics) {
    risk.push(
      { label: "Max drawdown (abs)", value: money(-analytics.maxDrawdownAbs), tone: "down" },
      { label: "Longest underwater", value: `${analytics.longestUnderwaterDays.toFixed(0)}d` },
      { label: "Sharpe", value: ratio(analytics.sharpeRatio) },
      { label: "Sortino", value: ratio(analytics.sortinoRatio) },
      { label: "Calmar", value: ratio(analytics.calmarRatio) },
      { label: "Recovery factor", value: ratio(analytics.recoveryFactor) },
      { label: "Daily VaR 95%", value: formatSignedPct(analytics.valueAtRisk95Pct), tone: "down" },
    );
  }

  const tradeStats: ReportStat[] = [
    { label: "Total trades", value: String(stats.totalTrades) },
    { label: "Win rate", value: `${stats.winRatePct.toFixed(1)}%` },
    {
      label: "Profit factor",
      value: !hasTrades ? "-" : stats.profitFactor === null ? "inf" : stats.profitFactor.toFixed(2),
    },
    { label: "Avg trade", value: formatSignedPct(stats.avgTradePct), tone: toneOf(stats.avgTradePct) },
    { label: "Avg win", value: formatSignedPct(stats.avgWinPct), tone: "up" },
    { label: "Avg loss", value: formatSignedPct(stats.avgLossPct), tone: "down" },
    { label: "Best trade", value: formatSignedPct(stats.bestTradePct), tone: toneOf(stats.bestTradePct) },
    { label: "Worst trade", value: formatSignedPct(stats.worstTradePct), tone: toneOf(stats.worstTradePct) },
  ];
  if (analytics) {
    tradeStats.push(
      { label: "Expectancy", value: money(analytics.expectancyAbs), tone: toneOf(analytics.expectancyAbs) },
      { label: "Payoff ratio", value: ratio(analytics.payoffRatio) },
      { label: "SQN", value: ratio(analytics.sqn) },
      {
        label: "Win / loss streak",
        value: `${analytics.maxConsecutiveWins} / ${analytics.maxConsecutiveLosses}`,
      },
    );
  }

  const sections: ReportSection[] = [
    { kind: "stats", title: "Returns", items: returns },
    { kind: "stats", title: "Risk", items: risk },
    { kind: "stats", title: "Trades", items: tradeStats },
  ];

  // Daily is what the on-screen report charts; the bar-by-bar curve only when there are no analytics.
  const equityPoints: [number, number][] = analytics
    ? analytics.daily.map((d) => [d.time, d.equity])
    : result.equityCurve.map((p) => [p.time, p.equity]);
  sections.push({
    kind: "chart",
    title: analytics ? "Equity vs buy & hold" : "Equity",
    series: [
      { label: input.strategyName, points: equityPoints, tone: "strategy" },
      ...(analytics
        ? [
            {
              label: analytics.benchmarkLabel ? `Hold ${analytics.benchmarkLabel}` : "Buy & hold",
              points: analytics.daily.map((d): [number, number] => [d.time, d.benchmark]),
              tone: "benchmark" as const,
            },
          ]
        : []),
    ],
    baseline: capital,
    format: moneyAxis,
    formatTime: formatDateCompact,
  });
  sections.push({
    kind: "chart",
    title: "Drawdown",
    height: 42,
    series: [
      {
        label: "Drawdown",
        points: analytics
          ? analytics.daily.map((d) => [d.time, d.drawdownPct])
          : drawdownSeries(equityPoints),
        tone: "drawdown",
        fill: true,
      },
    ],
    baseline: 0,
    format: pctAxis,
    formatTime: formatDateCompact,
  });

  if (analytics && analytics.monthlyReturns.length > 0) {
    const years = [...new Set(analytics.monthlyReturns.map((m) => m.year))].sort();
    sections.push({
      kind: "table",
      title: "Monthly returns",
      head: ["Year", ...MONTHS, "Total"],
      align: ["left", ...MONTHS.map(() => "right" as const), "right"],
      fontSize: 6.8,
      rows: years.map((year) => {
        const months = analytics.monthlyReturns.filter((m) => m.year === year);
        const total = (months.reduce((acc, m) => acc * (1 + m.returnPct / 100), 1) - 1) * 100;
        return [
          cell(String(year)),
          ...MONTHS.map((_, i) => {
            const m = months.find((x) => x.month === i + 1);
            return m ? cell(m.returnPct.toFixed(1), toneOf(m.returnPct)) : cell("");
          }),
          cell(total.toFixed(1), toneOf(total)),
        ];
      }),
      note: "Percent per calendar month; the last column compounds the year.",
    });
  }

  if (analytics && analytics.exitReasons.length > 0) {
    sections.push({
      kind: "table",
      title: "Exits by reason",
      head: ["Reason", "Trades", "Winners", "Win rate", "P&L"],
      align: ["left", "right", "right", "right", "right"],
      rows: analytics.exitReasons.map((r) => [
        cell(exitLabel(r.reason)),
        cell(String(r.count)),
        cell(String(r.winCount)),
        cell(r.count > 0 ? `${((r.winCount / r.count) * 100).toFixed(0)}%` : "-"),
        cell(money(r.pnl), toneOf(r.pnl)),
      ]),
    });
  }

  if (analytics && analytics.drawdownPeriods.length > 0) {
    sections.push({
      kind: "table",
      title: "Worst drawdowns",
      head: ["Started", "Bottom", "Recovered", "Depth", "Length"],
      align: ["left", "left", "left", "right", "right"],
      rows: analytics.drawdownPeriods.map((p) => [
        cell(formatDate(p.startTime)),
        cell(formatDate(p.valleyTime)),
        cell(p.endTime !== null ? formatDate(p.endTime) : "not yet"),
        cell(`${p.depthPct.toFixed(2)}%`, "down"),
        cell(`${p.durationDays.toFixed(0)}d`),
      ]),
    });
  }

  if (spec) sections.push(rulesSection(spec));
  sections.push(tradesTable(trades));

  return {
    kicker: "StrategyHive · Backtest report",
    title: input.title?.trim() || input.strategyName,
    subtitle: `${input.symbol} ${input.timeframe} · ${input.marketType}${
      start !== undefined && end !== undefined ? ` · ${formatDate(start)} -> ${formatDate(end)}` : ""
    }`,
    generatedAt: now(),
    meta,
    sections,
    footer: DISCLAIMER,
    filename: ["backtest-report", input.strategyName, input.symbol, input.timeframe],
  };
}

// --- live bot -----------------------------------------------------------------------------------

export interface BotReportInput {
  name: string;
  strategyName?: string | null;
  symbol: string;
  timeframe: string;
  marketType: string;
  status: string;
  startingCash: number;
  startedAt?: number | null;
  lastBarAt?: number | null;
  /** [time ms, equity] */
  equity: [number, number][];
  trades: readonly ReportTrade[];
  /**
   * How many trades the bot has closed in all, when `trades` is only the
   * latest of them (the hub sends the last 500). Trade statistics are then
   * labelled as covering only those.
   */
  totalTradeCount?: number;
  /** "long 0.0123 @ 64,210.00", or null when flat. */
  position?: string | null;
  /** Current equity, when it is known more freshly than the last equity point. */
  currentEquity?: number | null;
  realisedPnl?: number | null;
  /** The bot's own figure, positive; worked out from `equity` when not given. */
  maxDrawdownPct?: number | null;
  gaps?: { from: number; bars: number; cause: string }[];
  spec?: StrategySpec;
  extraMeta?: [string, string][];
}

export function botReport(input: BotReportInput): ReportDoc {
  const { trades } = input;
  const equityNow = input.currentEquity ?? input.equity[input.equity.length - 1]?.[1] ?? input.startingCash;
  const returnPct = ((equityNow - input.startingCash) / input.startingCash) * 100;
  const total = Math.max(input.totalTradeCount ?? trades.length, trades.length);
  const partial = total > trades.length;
  // With only the latest trades in hand, summing them is not the realised P&L;
  // flat, it is everything the equity has moved.
  const realised =
    input.realisedPnl ??
    (!partial
      ? trades.reduce((sum, t) => sum + t.pnl, 0)
      : !input.position
        ? equityNow - input.startingCash
        : null);
  const over = partial ? ` (last ${trades.length})` : "";
  const wins = trades.filter((t) => t.pnl > 0);
  const losses = trades.filter((t) => t.pnl < 0);
  const grossWin = wins.reduce((s, t) => s + t.pnl, 0);
  const grossLoss = -losses.reduce((s, t) => s + t.pnl, 0);
  const dd = drawdownSeries(input.equity);
  const maxDd =
    input.maxDrawdownPct !== null &&
    input.maxDrawdownPct !== undefined &&
    Number.isFinite(input.maxDrawdownPct)
      ? -Math.abs(input.maxDrawdownPct)
      : dd.reduce((worst, [, v]) => Math.min(worst, v), 0);
  const pcts = trades.map((t) => t.pnlPct * 100);
  const avg = (xs: number[]) => (xs.length > 0 ? xs.reduce((a, b) => a + b, 0) / xs.length : 0);
  const first = input.equity[0]?.[0] ?? input.startedAt ?? undefined;
  const last = input.lastBarAt ?? input.equity[input.equity.length - 1]?.[0];

  const meta: [string, string][] = [
    ["Strategy", input.strategyName ?? "-"],
    ["Market", `${input.symbol} ${input.timeframe} · ${input.marketType}`],
    ["Status", input.status],
    ["Starting cash", money(input.startingCash)],
    ["Record since", first !== undefined ? formatDateTime(first) : "-"],
    ["Last started", input.startedAt ? formatDateTime(input.startedAt) : "-"],
    ["Last bar", last ? formatDateTime(last) : "-"],
    ...(input.extraMeta ?? []),
  ];
  if (input.spec && effectiveLeverage(input.spec.risk) > 1) {
    meta.push(["Leverage", `${effectiveLeverage(input.spec.risk)}x`]);
  }

  const headline: ReportStat[] = [
    { label: "Equity", value: money(equityNow) },
    { label: "Return", value: formatSignedPct(returnPct), tone: toneOf(returnPct) },
    { label: "Realised P&L", value: realised === null ? "-" : money(realised), tone: toneOf(realised) },
    { label: "Max drawdown", value: `${maxDd.toFixed(2)}%`, tone: maxDd < 0 ? "down" : undefined },
    { label: "Position", value: input.position ?? "flat" },
    { label: "Closed trades", value: total.toLocaleString("en-US") },
    {
      label: `Win rate${over}`,
      value: trades.length > 0 ? `${((wins.length / trades.length) * 100).toFixed(1)}%` : "-",
    },
    {
      label: `Profit factor${over}`,
      value: trades.length === 0 ? "-" : grossLoss === 0 ? "inf" : (grossWin / grossLoss).toFixed(2),
    },
    {
      label: `Avg trade${over}`,
      value: trades.length > 0 ? formatSignedPct(avg(pcts)) : "-",
      tone: toneOf(avg(pcts)),
    },
    {
      label: `Best trade${over}`,
      value: trades.length > 0 ? formatSignedPct(Math.max(...pcts)) : "-",
      tone: trades.length > 0 ? toneOf(Math.max(...pcts)) : undefined,
    },
    {
      label: `Worst trade${over}`,
      value: trades.length > 0 ? formatSignedPct(Math.min(...pcts)) : "-",
      tone: trades.length > 0 ? toneOf(Math.min(...pcts)) : undefined,
    },
    { label: "Gaps in record", value: String(input.gaps?.length ?? 0) },
  ];

  const sections: ReportSection[] = [
    { kind: "stats", title: "Live performance", items: headline },
    {
      kind: "chart",
      title: "Equity",
      series: [{ label: input.name, points: input.equity, tone: "strategy" }],
      baseline: input.startingCash,
      format: moneyAxis,
      formatTime: formatDateCompact,
    },
    {
      kind: "chart",
      title: "Drawdown",
      height: 42,
      series: [{ label: "Drawdown", points: dd, tone: "drawdown", fill: true }],
      baseline: 0,
      format: pctAxis,
      formatTime: formatDateCompact,
    },
  ];
  if (input.spec) sections.push(rulesSection(input.spec));
  const table = tradesTable(trades, "Closed trades");
  if (partial) {
    table.title = `Closed trades (latest ${trades.length.toLocaleString("en-US")} of ${total.toLocaleString("en-US")})`;
    table.note = "The record holds every trade; this report carries the latest ones the hub sends.";
  }
  sections.push(table);
  if (input.gaps && input.gaps.length > 0) {
    sections.push({
      kind: "table",
      title: "Gaps in the record",
      head: ["From", "Bars missed", "Cause"],
      align: ["left", "right", "left"],
      rows: input.gaps.map((g) => [
        cell(formatDateTime(g.from)),
        cell(String(g.bars)),
        cell(g.cause === "offline" ? "Bot offline, replayed on resume" : "Feed lost the bars"),
      ]),
    });
  }

  return {
    kicker: "StrategyHive · Live paper trading report",
    title: input.name,
    subtitle: `${input.strategyName ? `${input.strategyName} · ` : ""}${input.symbol} ${input.timeframe} · ${input.marketType}`,
    generatedAt: now(),
    meta,
    sections,
    footer: DISCLAIMER,
    filename: ["live-report", input.name, input.symbol, input.timeframe],
  };
}
